import {
  AppRootElementBlueprint,
  createFrontendModule,
  PageLayout,
} from '@backstage/frontend-plugin-api';
import { SwappableComponentBlueprint } from '@backstage/plugin-app-react';
import visualizerPlugin from '@backstage/plugin-app-visualizer';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import techdocsPlugin from '@backstage/plugin-techdocs/alpha';
import userSettingsPlugin from '@backstage/plugin-user-settings/alpha';
import {
  waadooIcons,
  type WaadooIconName,
  ENTITY_TAB_TITLES,
} from '@internal/plugin-waadoo-core';
import { TabRowScroll } from './tabRowScroll';
import { WaadooPageLayout } from './WaadooPageLayout';

/**
 * The app's page layout (`WaadooPageLayout`), under the id of
 * @backstage/plugin-app's (`component:app/core-page-layout`): a module's
 * extension replaces the plugin's extension with the same id, in place.
 */
const pageLayout = SwappableComponentBlueprint.make({
  name: 'core-page-layout',
  params: define =>
    define({
      component: PageLayout,
      loader: () => WaadooPageLayout,
    }),
});

export const pageLayoutModule = createFrontendModule({
  pluginId: 'app',
  extensions: [pageLayout],
});

/**
 * The tab rows that scroll sideways on a phone (`tabRowScroll.ts`): one
 * element under the app's root, drawing nothing, that follows every row of
 * the document.
 */
const tabRowScroll = AppRootElementBlueprint.make({
  name: 'tab-row-scroll',
  params: { element: <TabRowScroll /> },
});

export const tabRowScrollModule = createFrontendModule({
  pluginId: 'app',
  extensions: [tabRowScroll],
});

/** A sub-page tab's icon, an element as the PageBlueprint passes it on. */
function tabIcon(name: WaadooIconName) {
  const Icon = waadooIcons[name];
  return <Icon aria-hidden focusable="false" />;
}

/**
 * The French titles and the icons of the settings page's sub-pages
 * (`/settings`, @backstage/plugin-user-settings alpha). Titles and icons
 * are the sub-pages' own params (SubPageBlueprint), overridden here: the
 * upstream titles are plain English strings, out of reach of the
 * translations (@waadoo/backstage-i18n), and the sub-pages declare no icon.
 * A `title` set in app-config (`app.extensions`) still wins over these.
 */
export const userSettingsTabsModule = createFrontendModule({
  pluginId: 'user-settings',
  extensions: [
    // The page's own title: the header's breadcrumb, the tab list's name,
    // and the sidebar's entry (« Paramètres », as frenchNavTitles already
    // rendered « Settings »).
    userSettingsPlugin.getExtension('page:user-settings').override({
      params: { title: 'Paramètres' },
    }),
    // Its content too: the upstream page with « Menu compact » in place of
    // « Pin Sidebar » (GeneralSettings.tsx). Loaded lazily, wrapped in
    // core-components' Content, as the upstream loader does.
    userSettingsPlugin.getExtension('sub-page:user-settings/general').override({
      params: {
        title: 'Général',
        icon: tabIcon('generalSettings'),
        loader: async () => {
          const [{ GeneralSettings }, { Content }] = await Promise.all([
            import('./GeneralSettings'),
            import('@backstage/core-components'),
          ]);
          return (
            <Content>
              <GeneralSettings />
            </Content>
          );
        },
      },
    }),
    userSettingsPlugin
      .getExtension('sub-page:user-settings/auth-providers')
      .override({
        params: {
          title: 'Fournisseurs d’authentification',
          icon: tabIcon('authProviders'),
        },
      }),
    userSettingsPlugin
      .getExtension('sub-page:user-settings/feature-flags')
      .override({
        params: {
          title: 'Fonctionnalités expérimentales',
          icon: tabIcon('featureFlags'),
        },
      }),
  ],
});

/**
 * The same for the app visualizer (`/visualizer`): its page's title — the
 * sidebar's entry too, « Visualizer » until now (no frenchNavTitles entry) —
 * and its sub-pages.
 */
export const visualizerTabsModule = createFrontendModule({
  pluginId: 'app-visualizer',
  extensions: [
    visualizerPlugin.getExtension('page:app-visualizer').override({
      params: { title: 'Visualiseur' },
    }),
    visualizerPlugin.getExtension('sub-page:app-visualizer/tree').override({
      params: { title: 'Arbre', icon: tabIcon('extensionTree') },
    }),
    visualizerPlugin.getExtension('sub-page:app-visualizer/details').override({
      params: { title: 'Détaillé', icon: tabIcon('extensionList') },
    }),
    visualizerPlugin.getExtension('sub-page:app-visualizer/text').override({
      params: { title: 'Texte', icon: tabIcon('extensionText') },
    }),
  ],
});

/**
 * The French titles of the catalog entity page's tabs that are not already
 * French: « Overview » (the catalog's own content) and « TechDocs » — plain
 * strings too. « Kubernetes » stays. No icon: the entity page's tab bar
 * drops the contents' icons (EntityHeaderLayoutProps); the Odoo project's
 * header draws its own (`entityTabIcon`).
 */
export const catalogEntityTabsModule = createFrontendModule({
  pluginId: 'catalog',
  extensions: [
    catalogPlugin.getExtension('entity-content:catalog/overview').override({
      params: { title: ENTITY_TAB_TITLES.overview },
    }),
  ],
});

export const techdocsEntityTabsModule = createFrontendModule({
  pluginId: 'techdocs',
  extensions: [
    techdocsPlugin.getExtension('entity-content:techdocs').override({
      params: { title: ENTITY_TAB_TITLES.documentation },
    }),
  ],
});
